import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Switch } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { Container, Button } from 'react-bootstrap';
import { setLanguage } from 'redux-i18n';
import ReactGA from 'react-ga4';
import { ToastContainer } from 'react-toastify';

import ChooseLanguageView from './containers/ChooseLanguageView';
import { routes } from './routes';

import { authAutoLogin, clearAuthLocalStorage } from './common/redux/actions/AuthenticationActions';
import { fetchJobFromNotification } from './common/redux/actions/JobsActions';
import { addNewsFromNotification } from './common/redux/actions/NewsActions';
import { getLanguages } from './common/redux/actions/LanguagesActions';
import { getPollsQuestions } from './common/redux/actions/PollsActions';
import { getSurveys } from './common/redux/actions/SurveysActions';
import { showPushNotificationToast } from './common/redux/actions/PushNotificationActions';
import { notifyJobApplication } from './common/redux/actions/ProfileActions';
import { getCountries } from './common/redux/actions/CountriesActions';

import { updateMomentLocale } from './common/i18n/utilities';
import './App.css';
import 'react-toastify/dist/ReactToastify.css';
import 'react-lazy-load-image-component/src/effects/blur.css';

const App = (props, context) => {
  const hasError = false;
  const {
    loading, jobs, lang, news
  } = useSelector((state) => ({
    isAuthenticated: state.AuthenticationState.token !== null,
    loading: state.ProfileState.loading || state.UsersState.loading || state.LanguagesState.loading,
    profile: state.ProfileState.data,
    jobs: state.JobsState,
    lang: state.i18nState.lang,
    news: state.NewsState,
    countries: state.CountriesState
  }));
  const dispatch = useDispatch();
  const t = context.t;

  const updateNotificationRelatedObject = async(notificationType, firebaseData) => {
    if (
      [
        'APPLICATION_VIEWED',
        'APPLICATION_DOCUMENTS_REQUESTED',
        'APPLICATION_DOWNLOADED',
        'APPLICATION_SHORTLISTED',
        'APPLICATION_OFFERED',
        'APPLICATION_REJECTED_BY_BUSINESS'
      ].includes(notificationType)
    ) {
      const applicationID = JSON.parse(firebaseData.application);

      await axios
        .get(`${process.env.REACT_APP_API_URL}/job-applications/${applicationID}/`)
        .then((res) => dispatch(notifyJobApplication(res.data)));
    } else if (notificationType === 'CONTENT_ADDED_NEWS' && news.fetched) {
      const newsItem = JSON.parse(firebaseData.news);
      dispatch(addNewsFromNotification(newsItem));
    } else if (notificationType === 'NEW_JOB_POSTED' && jobs.fetched) {
      dispatch(fetchJobFromNotification(firebaseData.job));
    }
  };

  const onFirebaseMessage = (message) => setTimeout(() => {
    if (message.data['firebase-messaging-msg-data'].notification) {
      const firebaseData = message.data['firebase-messaging-msg-data'];

      const notificationData = JSON.parse(firebaseData.data.notification_data);
      if (notificationData) {
        updateNotificationRelatedObject(notificationData.type, firebaseData.data);
      }
      dispatch(
        showPushNotificationToast({
          ...firebaseData.data,
          ...firebaseData.notification
        })
      );
    }
  }, 1000);

  useEffect(() => {
    const language = localStorage.getItem('language');
    if (language) {
      dispatch(setLanguage(language));
      updateMomentLocale(language);
    }
    dispatch(authAutoLogin())
      .catch((e) => {
        if (e.response && [400, 401, 404].includes(e.response.status)) {
          clearAuthLocalStorage();
          window.location.reload();
        }
      })
      .finally(() => {
        dispatch(getLanguages());
        dispatch(getPollsQuestions());
        dispatch(getSurveys());
        dispatch(getCountries());
      });

    console.log('Play Store URL: ', process.env.REACT_APP_ANDROID_URL);
    ReactGA.initialize(process.env.REACT_APP_ANALYTICS_MEASUREMENT_ID);

    navigator.serviceWorker && navigator.serviceWorker.addEventListener('message', onFirebaseMessage);

    return () => {
      navigator.serviceWorker && navigator.serviceWorker.removeEventListener('message', onFirebaseMessage);
    };
  }, []);

  const refreshLink = (
    <div>
      <br />
      <Button variant="link" className="gd-button button-modified" onClick={() => window.location.reload()}>
        {t('refresh the page')}
      </Button>
    </div>
  );

  if (hasError) {
    return (
      <Container
        style={{
          textAlign: 'center',
          wordBreak: lang === 'en' && 'break-word'
        }}
      >
        <div className="centeredBox" style={{ flexDirection: 'column' }}>
          <img src={'/static/media/wentWrong.png'} alt="" style={{ height: 150, display: 'block' }} />
          <p className="mt-3">{t('Ooops, something went wrong... Please try to {refresh}', { refresh: refreshLink })}</p>
        </div>
      </Container>
    );
  }

  const preferredLanguage = localStorage.getItem('language');

  return (
    <Container fluid={true} style={{ padding: 0, textAlign: 'center', height: '100%' }}>
      {loading ? (
        <div style={{ display: 'flex', height: '100%' }}>
          <img src={'/static/media/spinner.png'} alt="" className="LoadingSpinner" />
        </div>
      ) : preferredLanguage ? (
        <Switch>
          {routes.map((route, i) => {
            const { type: RouteType, ...rest } = route;
            return <RouteType key={i} {...rest} />;
          })}
        </Switch>
      ) : (
        <ChooseLanguageView />
      )}
      <ToastContainer position="top-center" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick />
    </Container>
  );
};

App.contextTypes = {
  t: PropTypes.func.isRequired
};

export default App;
